import { createContext, useContext } from 'react';
import type { Me, Meta, Permission, Scope } from '@lib/api';

/**
 * The signed-in person. Permission checks in the UI are convenience only (hide what cannot be done); the backend
 * enforces every one of them. `can()` reads `me.permissions` as /auth/me sent them, never the role.
 */
export interface Session {
  me: Me;
  meta: Meta;
  can: (permission: Permission) => boolean;
  logout: () => void;
  /** The account page changed who is signed in (the name, two-step verification): header, menu and every page follow. */
  updateMe: (patch: Partial<Pick<Me, 'name' | 'totp'>>) => void;
}

export const SessionCtx = createContext<Session | null>(null);
export const useSession = () => useContext(SessionCtx)!;

/** A permission check over a `Me` (an older backend without the list grants nothing). */
export const hasPermission = (me: Me, p: Permission) => Array.isArray(me.permissions) && me.permissions.includes(p);

/** Where the account's access reaches (an answer without one is read as national; the backend still decides). */
export const scopeOf = (me: Me): Scope => me.scope ?? { kind: 'national' };
export const isNational = (me: Me) => scopeOf(me).kind === 'national';
/** The province every list is locked to (a province or a location account), or null for a national one. */
export function scopeProvince(me: Me): { code: string; name: string } | null {
  const s = scopeOf(me);
  return s.kind === 'national' ? null : { code: s.provinceCode, name: s.provinceName };
}
/** The one location of a location account (an operator), or null. */
export function scopeLocation(me: Me): { id: number; name: string } | null {
  const s = scopeOf(me);
  return s.kind === 'location' ? { id: s.locationId, name: s.locationName } : null;
}
/**
 * Operations mode (/operations) is for location accounts (operators): 1 account = 1 checkpoint, the account's
 * location is where they work. Everyone else never enters it, so links into it are shown to them only.
 */
export const usesOperations = (me: Me) => scopeOf(me).kind === 'location' && !usesCourier(me);
/**
 * A courier (`order.deliver`) works from one Hub but on the road: its home is the list of its deliveries in the
 * regular shell (phone first), never the Reader-driven operations mode.
 */
export const usesCourier = (me: Me) => hasPermission(me, 'order.deliver');
